import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { getDatabase, get, ref } from 'firebase/database';
import { useUsersData } from '../../Helpers/UsersContext';
import { useTodos } from '../todos';
import { analyticsApi } from '../../api/analyticsApi';
import { NavigationMenuItem, isFeatureVisible, isMainNavActive, mainNavDefinitions, withTenantsLabel } from '../../Navigation/navigationConfig';
import { useFeatures } from '../useFeatures';
import { useTenantTerm } from '../useTenantTerm';

export interface MainNavItem {
    label: string;
    path: string;
    icon: string;
    isActive: boolean;
    /** Sub-pages: rendered as a flyout / drawer section instead of a link. */
    children?: NavigationMenuItem[];
}

interface UseNavigationDataReturn {
    userName: string;
    organizationName: string;
    customerLogo: string | undefined;
    isAdmin: boolean;
    isSuperadmin: boolean;
    mainNavItems: MainNavItem[];
    activeTodosCount: number;
    logOut: () => Promise<void>;
}

/**
 * Shared data/state for the app shell navigation: current user, organization
 * branding, admin flags, the main nav item list (with active-state derived
 * from the route) and the logged-in user's active todo count. Both the
 * desktop sidebar and the mobile drawer render from this single hook so
 * their content can't drift apart.
 */
export const useNavigationData = (): UseNavigationDataReturn => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();
    const database = getDatabase();
    const { users } = useUsersData();

    const [userName, setUserName] = useState('\u00A0');
    const [organizationName, setOrganizationName] = useState('\u00A0');
    const [customerLogo, setCustomerLogo] = useState<string | undefined>(undefined);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperadmin, setIsSuperadmin] = useState(false);

    useEffect(() => {
        const user = auth.currentUser;
        if (user == null) {
            navigate('/');
        } else {
            const userNameRef = ref(database, `users/userList/${user.uid}/name`);
            get(userNameRef)
                .then((snapshot) => {
                    if (snapshot.exists()) {
                        setUserName(snapshot.val());
                    } else {
                        setUserName('Användarnamn saknas');
                    }
                })
                .catch((error) => {
                    console.error('Error fetching user name:', error);
                    setUserName('Användarnamn saknas');
                });

            const organizationRef = ref(database, 'global/instanceName');
            get(organizationRef)
                .then((snapshot) => {
                    if (snapshot.exists()) {
                        setOrganizationName(snapshot.val());
                    } else {
                        setOrganizationName('Odefinierad');
                    }
                })
                .catch((error) => {
                    console.error('Error fetching organization name:', error);
                    setOrganizationName('Odefinierad');
                });

            // Fetch small logo from publicSettings (configured in login page settings)
            const smallLogoConfigRef = ref(database, 'publicSettings/loginPage/smallLogoUrl');
            get(smallLogoConfigRef)
                .then((snapshot) => {
                    if (snapshot.exists() && snapshot.val()) {
                        setCustomerLogo(snapshot.val());
                    } else {
                        // No small logo configured – we'll fall back to a generic profile icon
                        setCustomerLogo(undefined);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching small logo:', error);
                    setCustomerLogo(undefined);
                });
        }
    }, [auth.currentUser, navigate, database]);

    // Check if current user is admin/superadmin and identify for analytics
    useEffect(() => {
        if (auth.currentUser && users) {
            const currentUser = users.find((u) => u.key === auth.currentUser?.uid);
            // Superadmins have all admin privileges
            const userIsAdmin = currentUser?.admin || currentUser?.superadmin || false;
            setIsAdmin(userIsAdmin);
            setIsSuperadmin(currentUser?.superadmin || false);

            // Identify user for analytics
            analyticsApi.identifyUser(auth.currentUser.uid, userIsAdmin);
        }
    }, [auth.currentUser, users]);

    // Get user's active todos count
    const { activeTodos } = useTodos({ userId: auth.currentUser?.uid });
    const activeTodosCount = activeTodos.length;

    const features = useFeatures();
    const tenantsLabel = useTenantTerm().plural;
    const mainNavItems = useMemo<MainNavItem[]>(
        () =>
            mainNavDefinitions
                .filter((definition) => (!definition.adminOnly || isAdmin) && isFeatureVisible(definition.feature, features))
                .map((definition) => ({
                    label: definition.label,
                    path: definition.path,
                    icon: definition.icon,
                    isActive: isMainNavActive(definition, location.pathname),
                    children: definition.children && withTenantsLabel(definition.children, tenantsLabel),
                })),
        [location.pathname, isAdmin, features, tenantsLabel],
    );

    const logOut = async () => {
        analyticsApi.clearUser();
        try {
            await signOut(auth);
            navigate('/home');
        } catch (error) {
            console.error(error);
        }
    };

    return {
        userName,
        organizationName,
        customerLogo,
        isAdmin,
        isSuperadmin,
        mainNavItems,
        activeTodosCount,
        logOut,
    };
};
